import i18n from 'i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

const version = (import.meta as any).env?.VITE_I18N_VER ?? 'dev';
const LOCK = (import.meta as any).env?.VITE_LOCK_LANGUAGE?.trim?.();

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'sk',
    supportedLngs: ['sk', 'en'],
    ns: ['common', 'nav', 'plans', 'profile', 'reminders', 'tasks', 'filters', 'billing', 'dashboard', 'notifications', 'validation', 'pricing'],
    defaultNS: 'common',
    detection: {
      // Prefer URL path, then explicit signals, then persisted choice, then browser
      order: ['querystring', 'localStorage', 'cookie', 'htmlTag', 'navigator'],
      // Support both ?lng= and ?lang=
      lookupQuerystring: 'lang',
      caches: ['localStorage'],
    },
    backend: {
      loadPath: `/locales/{{lng}}/{{ns}}.json?v=${version}`,
    },
    interpolation: { escapeValue: false },
    react: {
      useSuspense: false,
      bindI18n: 'languageChanged loaded',
      transSupportBasicHtmlNodes: true,
    },
    cleanCode: true,
    nonExplicitSupportedLngs: true,
    debug: (import.meta as any).env?.DEV,
  });

// Custom language detection from URL path
function detectLanguageFromPath() {
  if (typeof window === 'undefined') return undefined;
  
  const path = window.location.pathname;
  const pathSegments = path.split('/').filter(Boolean);
  
  // Check if first segment is a language code
  if (pathSegments.length > 0) {
    const firstSegment = pathSegments[0];
    if (['sk', 'en'].includes(firstSegment)) {
      return firstSegment;
    }
  }
  
  return undefined;
}

// Debug event listeners
i18n.on('initialized', (opts) => {
  console.log('[i18n] initialized', opts);
  
  // Check URL path for language
  const pathLanguage = detectLanguageFromPath();
  if (pathLanguage && i18n.language !== pathLanguage) {
    console.log('[i18n] detected language from URL path:', pathLanguage);
    i18n.changeLanguage(pathLanguage);
  }
  
  // Optional hard lock jazyka (napr. pre produkciu): VITE_LOCK_LANGUAGE=sk|en
  const lock = LOCK as 'sk' | 'en' | undefined;
  if (lock) {
    try { localStorage.removeItem('i18nextLng'); } catch {}
    if (i18n.language !== lock) {
      console.log('[i18n] locking language to', lock);
      i18n.changeLanguage(lock);
    }
  }
});
i18n.on('loaded', (loaded) => console.log('[i18n] loaded', loaded));
i18n.on('languageChanged', (lng) => console.log('[i18n] languageChanged →', lng));
i18n.on('failedLoading', (lng, ns, msg) =>
  console.error('[i18n] failedLoading', { lng, ns, msg })
);

// Nastavenie lang atribútu HTML elementu pri zmene jazyka
i18n.on('languageChanged', (lng: string) => {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lng;
  }
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('i18nextLng', lng);
  }
});

// Inicializácia lang atribútu
if (typeof document !== 'undefined') {
  document.documentElement.lang = 'sk';
}

export const i18nReady = Promise.resolve(i18n);

// Helper pre i18n objekty v JSX
export type MaybeI18n = string | { [k: string]: string } | null | undefined;

export function text(v: MaybeI18n, locale: 'sk' | 'en' = 'sk'): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  const o = v as Record<string, string>;
  return o[locale] ?? o.en ?? Object.values(o)[0] ?? '';
}

export default i18n;
